import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UserPlus, KeyRound, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/Brand";
import { api } from "@/lib/api";
import { LandingModules } from "@/components/landing/LandingModules";
import { LandingPlans } from "@/components/landing/LandingPlans";
import { captureRef } from "@/lib/referral";

const HERO = "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const STEPS = [
  [UserPlus, "Faça seu cadastro", "Informe os dados do seu negócio em poucos minutos."],
  [KeyRound, "Receba seu número de acesso", "Um número exclusivo chega na tela, no e-mail e no WhatsApp."],
  [LogIn, "Entre e comece a vender", "Use o número de acesso com seu login e senha."],
];

function Header() {
  return (
    <header className="absolute inset-x-0 top-0 z-20">
      <div className="mx-auto max-w-7xl flex items-center justify-between px-5 sm:px-8 py-5">
        <div className="flex items-center gap-3 text-white">
          <BrandMark testId="landing-brand" />
          <span className="font-display text-lg font-bold">FSCsistemas</span>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="ghost" className="text-white hover:bg-white/10 hover:text-white" data-testid="landing-login-btn"><Link to="/login">Entrar</Link></Button>
          <Button asChild className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="landing-header-signup-btn"><Link to="/cadastro">Teste grátis</Link></Button>
        </div>
      </div>
    </header>
  );
}

// Textos do topo: editáveis no Admin Master (Parâmetros); estes são os padrões, usados até a resposta chegar
export const TEXTOS_PADRAO = {
  landing_eyebrow: "Sistema PDV na nuvem",
  landing_title: "Venda mais, organize seu negócio e tenha o controle completo da sua operação em um só lugar.",
  landing_subtitle: "Com nosso sistema PDV, você gerencia vendas, comandas, mesas, tickets, estacionamento, cardápio digital e delivery de forma simples, rápida e totalmente online.",
  landing_footer: "Sem complicação. Seu negócio no controle.",
};

function Hero({ days, textos = TEXTOS_PADRAO }) {
  return (
    <section className="relative min-h-[88vh] flex items-end overflow-hidden bg-slate-950">
      <img src={HERO} alt="Atendimento com PDV no balcão" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pb-20 pt-32">
        <div className="max-w-2xl space-y-6 text-white">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-300 fade-up" data-testid="landing-eyebrow">{textos.landing_eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] fade-up" style={{ animationDelay: "60ms" }} data-testid="landing-title">{textos.landing_title}</h1>
          <p className="text-base md:text-lg text-slate-300 fade-up" style={{ animationDelay: "120ms" }} data-testid="landing-subtitle">{textos.landing_subtitle}</p>
          <div className="flex flex-wrap gap-3 fade-up" style={{ animationDelay: "180ms" }}>
            <Button asChild size="lg" className="h-14 px-7 text-base bg-blue-500 hover:bg-blue-600 text-white" data-testid="landing-cta-btn"><Link to="/cadastro">Cadastre-se gratuitamente e teste por {days} dias<ArrowRight className="h-5 w-5 ml-2" /></Link></Button>
          </div>
          <p className="text-sm text-slate-400" data-testid="landing-footer">{textos.landing_footer}</p>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [days, setDays] = useState(15);
  const [textos, setTextos] = useState(TEXTOS_PADRAO);
  useEffect(() => {
    api.get("/platform-info").then((r) => {
      setDays(r.data.trial_days);
      setTextos(Object.fromEntries(Object.keys(TEXTOS_PADRAO).map((k) => [k, (r.data[k] || "").trim() || TEXTOS_PADRAO[k]])));
    }).catch(() => {});
  }, []);
  useEffect(() => { captureRef(window.location.search); }, []);
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Header />
      <Hero days={days} textos={textos} />
      <LandingModules />
      <section className="border-y bg-card/50">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-3 gap-10">
          <div>
            <p className="eyebrow mb-2">Como funciona</p>
            <h2 className="text-3xl font-bold">Faça seu cadastro e aproveite {days} dias grátis para testar todos os recursos.</h2>
          </div>
          <ol className="lg:col-span-2 grid sm:grid-cols-3 gap-6">
            {STEPS.map(([Icon, title, text], i) => (
              <li key={title} className="space-y-3">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/15 text-primary"><Icon className="h-5 w-5" /></span>
                <p className="font-mono text-xs text-muted-foreground">0{i + 1}</p>
                <p className="font-semibold">{title}</p>
                <p className="text-sm text-muted-foreground">{text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <LandingPlans />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <div className="rounded-3xl bg-primary text-primary-foreground p-8 sm:p-12 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <h2 className="text-3xl font-bold">Comece agora e leve seu negócio para o próximo nível.</h2>
            <p className="opacity-80 mt-2">Após o cadastro, você receberá um número de acesso exclusivo para usar junto com seu login e senha.</p>
          </div>
          <Button asChild size="lg" variant="secondary" className="h-14 px-7 text-base" data-testid="landing-bottom-cta-btn"><Link to="/cadastro">Cadastre-se grátis</Link></Button>
        </div>
      </section>
      <footer className="border-t py-8 text-center text-sm text-muted-foreground">FSCsistemas · Sem complicação. Seu negócio no controle.</footer>
    </div>
  );
}
